import React, { useState } from 'react';
import { MapContainer, Marker } from 'react-leaflet';
import { FiExternalLink, FiMapPin, FiPhone, FiMail, FiEdit2 } from 'react-icons/fi';
import { BasemapLayers, BasemapToggle } from './MapBasemaps';
import JobFieldEditModal from './JobFieldEditModal';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Idempotent global marker-icon fix (matches the rest of the app)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const STATUS_COLORS = {
  'Pending': '#fbbf24', 'Scheduled': '#60a5fa',
  'In Progress - Office Work': '#818cf8',
  'In Progress - Initial Field Work': '#a78bfa',
  'In Progress - Topo': '#a78bfa',
  'In Progress - Additional Field Work Required': '#7c3aed',
  'In Progress - Drafting': '#8b5cf6',
  'Survey Complete': '#34d399', 'Needs Review': '#f97316',
  'Sent for Review': '#fb923c', 'Approved': '#22c55e',
  'Approved - Internal Review': '#4ade80', 'Approved - Planning & Zoning': '#22c55e',
  'Submitted for Recording': '#14b8a6', 'Recorded': '#0d9488',
  'Final Mark Up / Stake Lines': '#ec4899', 'Needs Invoice': '#eab308',
  'Invoiced': '#fb923c', 'Paid': '#10b981', 'Cancelled': '#9ca3af', 'On Hold': '#64748b',
};
const statusColor = (s) => STATUS_COLORS[s] || '#6b7280';

const fmtMoney = (n) => '$' + (Number(n) || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
const fmtDate = (d) => d ? new Date(d + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : null;
const fmtTime = (t) => {
  if (!t) return null;
  const [h, m] = t.split(':');
  const hr = (parseInt(h, 10) % 12) || 12;
  const ampm = parseInt(h, 10) < 12 ? 'AM' : 'PM';
  return `${hr}:${m} ${ampm}`;
};

// A labeled group of label/value rows; renders only rows that have a value
function InfoGroup({ title, rows, onEdit }) {
  const visible = rows.filter(r => r && r.value != null && r.value !== '');
  if (visible.length === 0 && !onEdit) return null;
  return (
    <div className="jo-group">
      <div className="jo-group-title">
        <span>{title}</span>
        {onEdit && (
          <button className="qv-edit-btn" onClick={onEdit} title={`Edit ${title}`}>
            <FiEdit2 size={13} />
          </button>
        )}
      </div>
      {visible.length > 0
        ? visible.map((r, i) => (
            <div className="jo-row" key={i}>
              <span className="jo-label">{r.label}</span>
              <span className="jo-value">{r.value}</span>
            </div>
          ))
        : <div className="qv-empty-hint">No details yet — click ✎ to add</div>}
    </div>
  );
}

const JO_PRIORITY_OPTIONS = ['Low', 'Normal', 'High', 'Urgent'];
const JO_SERVICE_TYPE_OPTIONS = [
  '', 'Boundary', 'Topographic', 'ALTA', 'Construction Stakeout', 'As-Built',
  'Foundation', 'Subdivision/Plat', 'Elevation Certificate', 'Mortgage Inspection', 'Site Plan', 'Other'
];
// What the crew reported at close-out. Stored as short codes; these are the words.
// Must stay in step with MONUMENT_RECOVERY_VALUES / SITE_CONDITION_VALUES in app.py
// and with the options in RequiredCommentModal.
const JO_MONUMENT_OPTIONS = [
  { value: '', label: '' },
  { value: 'most', label: 'Found most' },
  { value: 'some', label: 'Found some' },
  { value: 'none', label: 'Almost none — rebuilt from deeds' },
];
const JO_SITE_OPTIONS = [
  { value: '', label: '' },
  { value: 'open', label: 'Open — clear shots' },
  { value: 'mixed', label: 'Mixed' },
  { value: 'heavy', label: 'Heavy — cutting line / no GPS' },
];
const joLabel = (opts, v) => (opts.find(o => o.value === v) || {}).label || null;

const JO_SECTIONS = {
  conditions: { title: 'Field Conditions', fields: [
    { key: 'corners_count', label: 'Corners Set / Recovered', type: 'number' },
    { key: 'monument_recovery', label: 'Existing Monuments', type: 'select', options: JO_MONUMENT_OPTIONS },
    { key: 'site_conditions', label: 'Terrain & Vegetation', type: 'select', options: JO_SITE_OPTIONS },
  ] },
  property: { title: 'Property & Parcel', fields: [
    { key: 'service_type', label: 'Service Type', type: 'select', options: JO_SERVICE_TYPE_OPTIONS },
    { key: 'location_city_state_zip', label: 'City/State/Zip', type: 'text' },
    { key: 'subdivision', label: 'Subdivision', type: 'text' },
    { key: 'county', label: 'County', type: 'text' },
    { key: 'parcel', label: 'Parcel', type: 'text' },
    // Acreage actually surveyed. qPublic often reports the parent tract a survey is
    // being cut out of, so this override exists to correct it. Blank hands the job
    // back to the derived value (linked quote, then parcel records).
    { key: 'acres', label: 'Acres Surveyed', type: 'number' },
    { key: 'land_lot', label: 'Land Lot', type: 'text' },
    { key: 'district', label: 'District', type: 'text' },
    { key: 'section', label: 'Section', type: 'text' },
  ] },
  dates: { title: 'Dates & Tracking', fields: [
    { key: 'priority', label: 'Priority', type: 'select', options: JO_PRIORITY_OPTIONS },
    { key: 'work_ordered_date', label: 'Work Ordered', type: 'date' },
    { key: 'scheduled_date', label: 'Scheduled', type: 'date' },
    { key: 'work_started_date', label: 'Started', type: 'date' },
    { key: 'work_finished_date', label: 'Finished', type: 'date' },
    { key: 'completion_date', label: 'Completed', type: 'date' },
  ] },
  contacts: { title: 'On-Site Contact', fields: [
    { key: 'location_person', label: 'On-Site Contact', type: 'text' },
  ] },
};

export default function JobOverview({ job, profitability, canManagePayroll, statuses = [], onStatusChange, onUpdated }) {
  const [basemap, setBasemap] = useState('satellite');
  const [editSection, setEditSection] = useState(null);
  const lat = job.latitude != null ? Number(job.latitude) : null;
  const lng = job.longitude != null ? Number(job.longitude) : null;
  const hasCoords = Number.isFinite(lat) && Number.isFinite(lng);
  const center = hasCoords ? [lat, lng] : null;

  const schedule = job.scheduled_date
    ? `${fmtDate(job.scheduled_date)}${job.scheduled_time ? ' · ' + fmtTime(job.scheduled_time) : ''}`
    : null;

  return (
    <div className="job-overview dash-panel">
      {/* Top bar: identity + quick status */}
      <div className="jo-topbar">
        <div className="jo-identity">
          <div className="jo-jobnum">{job.job_number}</div>
          <div className="jo-client">{job.client_name}</div>
          {job.property_address && (
            <div className="jo-addr"><FiMapPin size={13} /> {job.property_address}</div>
          )}
        </div>
        <div className="jo-status-box">
          <span className="jo-status-label">STATUS</span>
          {onStatusChange ? (
            <select
              className="jo-status-select"
              value={job.status || ''}
              onChange={(e) => onStatusChange(e.target.value)}
              style={{ borderColor: statusColor(job.status), color: statusColor(job.status) }}
            >
              {statuses.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          ) : (
            <span className="jo-status-badge" style={{ background: statusColor(job.status) }}>{job.status}</span>
          )}
        </div>
      </div>

      <div className="jo-grid">
        {/* Map column */}
        <div className="jo-map-col">
          {hasCoords ? (
            <div className="jo-map">
              <MapContainer center={center} zoom={15} style={{ height: '100%', width: '100%' }} scrollWheelZoom={false} dragging={true}>
                <BasemapLayers basemap={basemap} />
                <Marker position={center} />
              </MapContainer>
              <BasemapToggle basemap={basemap} setBasemap={setBasemap} />
            </div>
          ) : (
            <div className="jo-map jo-map-empty">
              <FiMapPin size={28} />
              <span>No location set</span>
            </div>
          )}
          {hasCoords && (
            <div className="jo-coords">
              {lat.toFixed(6)}, {lng.toFixed(6)}
            </div>
          )}
          <div className="jo-map-links">
            {job.property_address && (
              <a href={`https://maps.google.com/?q=${encodeURIComponent(job.property_address)}`} target="_blank" rel="noopener noreferrer">
                <FiMapPin size={12} /> Directions
              </a>
            )}
            {job.parcel_url && (
              <a href={job.parcel_url} target="_blank" rel="noopener noreferrer">
                <FiExternalLink size={12} /> qPublic
              </a>
            )}
          </div>
        </div>

        {/* Property & Parcel */}
        <InfoGroup title="PROPERTY & PARCEL" onEdit={() => setEditSection(JO_SECTIONS.property)} rows={[
          { label: 'Service Type', value: job.service_type },
          { label: 'Subdivision', value: job.subdivision },
          { label: 'City/State/Zip', value: job.location_city_state_zip },
          { label: 'County', value: job.county },
          { label: 'Parcel', value: job.parcel },
          // Shows the derived value when nobody has corrected it, labelled with where
          // it came from — a quote/parcel figure may be the parent tract, so it should
          // never look as trustworthy as a hand-entered one.
          { label: 'Acres Surveyed', value: job.acres != null
              ? `${job.acres}`
              : (job.acres_effective != null
                  ? `${job.acres_effective} (from ${job.acres_source === 'quote' ? 'quote' : 'parcel records'} — verify)`
                  : null) },
          { label: 'Land Lot', value: job.land_lot },
          { label: 'District', value: job.district },
          { label: 'Section', value: job.section },
        ]} />

        {/* Field Conditions — what the crew reported at close-out. Shown even when
            empty so it's obvious the question hasn't been answered yet, and so a bad
            answer can be spotted and corrected here rather than in the database. */}
        <InfoGroup title="FIELD CONDITIONS" onEdit={() => setEditSection(JO_SECTIONS.conditions)} rows={[
          { label: 'Corners Set / Recovered', value: job.corners_count != null ? String(job.corners_count) : null },
          { label: 'Existing Monuments', value: joLabel(JO_MONUMENT_OPTIONS, job.monument_recovery) },
          { label: 'Terrain & Vegetation', value: joLabel(JO_SITE_OPTIONS, job.site_conditions) },
          { label: 'Reported', value: job.conditions_recorded_at
              ? new Date(job.conditions_recorded_at + (job.conditions_recorded_at.endsWith('Z') ? '' : 'Z')).toLocaleDateString()
              : null },
        ]} />

        {/* Dates & Tracking */}
        <InfoGroup title="DATES & TRACKING" onEdit={() => setEditSection(JO_SECTIONS.dates)} rows={[
          { label: 'Priority', value: job.priority },
          { label: 'Work Ordered', value: fmtDate(job.work_ordered_date) },
          { label: 'Scheduled', value: schedule },
          { label: 'Started', value: fmtDate(job.work_started_date) },
          { label: 'Finished', value: fmtDate(job.work_finished_date) },
          { label: 'Completed', value: fmtDate(job.completion_date) },
        ]} />

        {/* Contacts */}
        <InfoGroup title="CONTACTS" onEdit={() => setEditSection(JO_SECTIONS.contacts)} rows={[
          { label: 'Client', value: job.client_name },
          { label: 'On-Site', value: job.location_person },
          {
            label: 'Phone',
            value: job.client_phone ? <a href={`tel:${job.client_phone}`}><FiPhone size={11} /> {job.client_phone}</a> : null
          },
          {
            label: 'Email',
            value: job.client_email ? <a href={`mailto:${job.client_email}`}><FiMail size={11} /> {job.client_email}</a> : null
          },
          { label: 'Team', value: (job.team_members && job.team_members.length) ? job.team_members.map(m => m.name).join(', ') : null },
        ]} />

        {/* Billing & Balance */}
        <InfoGroup title="BILLING & BALANCE" rows={[
          { label: 'Contract', value: job.contract_amount ? fmtMoney(job.contract_amount) : null },
          { label: 'Change Orders', value: job.change_orders_total ? fmtMoney(job.change_orders_total) : null },
          { label: 'Total Value', value: job.total_contract_value ? fmtMoney(job.total_contract_value) : null },
          { label: 'Billing Co.', value: job.billing_company },
          { label: 'Bill To', value: job.billing_person },
          { label: 'Billing Email', value: job.billing_email },
        ]} />
      </div>

      {/* Profitability strip (payroll managers only) */}
      {canManagePayroll && profitability && (
        <div className="jo-profit">
          <div className="jo-profit-card">
            <div className="jo-profit-val">{fmtMoney(profitability.revenue)}</div>
            <div className="jo-profit-lbl">Revenue</div>
          </div>
          <div className="jo-profit-card">
            <div className="jo-profit-val">{fmtMoney(profitability.total_costs)}</div>
            <div className="jo-profit-lbl">Total Costs</div>
          </div>
          <div className="jo-profit-card">
            <div className="jo-profit-val" style={{ color: profitability.profit < 0 ? '#dc2626' : '#059669' }}>
              {fmtMoney(profitability.profit)}
            </div>
            <div className="jo-profit-lbl">Profit</div>
          </div>
          <div className="jo-profit-card">
            <div className="jo-profit-val" style={{ color: profitability.margin_percent < 0 ? '#dc2626' : '#1f2937' }}>
              {profitability.margin_percent}%
            </div>
            <div className="jo-profit-lbl">Margin</div>
          </div>
          {profitability.costs && (
            <div className="jo-profit-card jo-profit-breakdown">
              <div className="jo-profit-lbl">Cost Breakdown</div>
              <div className="jo-breakdown-row"><span>Labor ({profitability.costs.labor_hours}h)</span><span>{fmtMoney(profitability.costs.labor)}</span></div>
              {profitability.costs.mileage > 0 && <div className="jo-breakdown-row"><span>Mileage</span><span>{fmtMoney(profitability.costs.mileage)}</span></div>}
              {profitability.costs.materials > 0 && <div className="jo-breakdown-row"><span>Materials</span><span>{fmtMoney(profitability.costs.materials)}</span></div>}
              {profitability.costs.overhead > 0 && <div className="jo-breakdown-row"><span>Overhead</span><span>{fmtMoney(profitability.costs.overhead)}</span></div>}
            </div>
          )}
        </div>
      )}

      {editSection && (
        <JobFieldEditModal
          jobId={job.id}
          title={editSection.title}
          fields={editSection.fields}
          job={job}
          onClose={() => setEditSection(null)}
          onSaved={onUpdated}
        />
      )}
    </div>
  );
}
